import {
  ICustomFieldRecordSchema,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  QuoteOfferingGroup,
  QuoteOfferingGroupRequest,
  QuoteOfferingGroupTypeSchema,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { formatCurrency, pluralize, toDateShort } from '@monetize/utils/core';
import isEqual from 'lodash/isEqual';
import {
  formatQuoteSubtotal,
  getQuoteOfferingGroupLabel,
  sanitizeQuoteOfferingGroupMemberIds,
} from '../../../../../utils/quotes';

/**
 * One staged group in the Manage Groups modal. The modal is a scratchpad: rows
 * are edited freely with no API calls, and Save diffs them against the groups
 * the server currently has (see buildManageGroupsDraftPlan).
 */
export type ManageGroupsDraftRow = {
  /** Stable react key — an existing group's id, or `new-N` for an added row */
  key: string;
  /** Set only when the row maps to a group that already exists server-side */
  groupId?: string;
  /**
   * Carried through from the server group so the row knows whether it may be
   * deleted or have its accounts re-pointed — see
   * isQuoteOfferingGroupAddedOnQuote. Never set on a row added in the modal.
   */
  referenceGroupId?: string | null;
  groupType: QuoteOfferingGroupTypeSchema;
  shippingAccountId: string;
  shippingContactId: string;
  billingAccountId: string;
  billingContactId: string;
  /** Root quote offering ids only — segments follow their parent server-side */
  quoteOfferingIds: string[];
  /**
   * QUOTE_OFFERING_GROUP custom fields. Seeded from the server and sent back on
   * every write, since a PUT declares the whole group — omitting them would wipe
   * whatever was already set.
   */
  customFields?: ICustomFieldRecordSchema | null;
};

export type ManageGroupsDraftPlan = {
  deletes: { quoteOfferingGroupId: string; label: string }[];
  updates: {
    quoteOfferingGroupId: string;
    rowKey: string;
    label: string;
    payload: QuoteOfferingGroupRequest;
  }[];
  creates: {
    rowKey: string;
    label: string;
    payload: QuoteOfferingGroupRequest;
  }[];
};

/** What a card's pending change is, so each row can show it */
export type ManageGroupsRowStatus = 'new' | 'edited';

/** Row label shown in the modal and used in save failure messages */
export const getManageGroupsRowLabel = (rowIndex: number) =>
  `Group ${rowIndex + 1}`;

/**
 * Offerings eligible for group membership: top level only. Child offerings are
 * ramp/scheduled-change segments, which the API rejects in a membership list.
 */
export const getRootQuoteOfferings = (
  quoteOfferings: Maybe<IQuoteOfferingRespSchema[]>,
) =>
  (quoteOfferings || []).filter(
    ({ parentQuoteOfferingId }) => !parentQuoteOfferingId,
  );

export const getQuoteOfferingDisplayName = (
  quoteOffering: IQuoteOfferingRespSchema,
) =>
  quoteOffering.rateName
    ? `${quoteOffering.offeringName} - ${quoteOffering.rateName}`
    : quoteOffering.offeringName;

export type ManageGroupsOfferingDetail = {
  /**
   * Term, plus the ramp count when the offering has segments — e.g.
   * `Jan 1, 2026 - Dec 31, 2028 | 2 ramps`. Empty when no term is set yet.
   */
  term: string;
  amount: string;
};

/**
 * Term and amount for an offering, shown under its name wherever offerings are
 * listed or dragged. A quote can carry the same offering more than once on
 * different terms, so the name alone doesn't identify which one you're moving.
 *
 * Both figures cover the offering's ramp segments as well as the offering
 * itself: the amount is the sum (matching getOfferingAmounts in
 * QuoteOfferingAmount), and the term runs from the root's start to the last
 * segment's end, which is the span a ramped offering actually covers.
 */
export const getQuoteOfferingDisplayDetail = (
  quoteOffering: IQuoteOfferingRespSchema,
  quoteOfferings: Maybe<IQuoteOfferingRespSchema[]>,
  currency?: string,
): ManageGroupsOfferingDetail => {
  const segments = (quoteOfferings || []).filter(
    ({ parentQuoteOfferingId }) => parentQuoteOfferingId === quoteOffering.id,
  );

  const amount = segments.reduce(
    (total, { amount: segmentAmount }) => total + segmentAmount,
    quoteOffering.amount || 0,
  );

  // `yyyy-mm-dd` sorts lexicographically, so the latest end date needs no Date
  // parsing — which would only invite timezone drift into a display string.
  const endDate = [
    quoteOffering.endDate,
    ...segments.map(({ endDate: segmentEndDate }) => segmentEndDate),
  ]
    .filter(Boolean)
    .sort()
    .pop();

  return {
    term: [
      quoteOffering.startDate && endDate
        ? `${toDateShort(quoteOffering.startDate)} - ${toDateShort(endDate)}`
        : '',
      // Flags that the span covers segments rather than a single flat term
      segments.length
        ? `${segments.length} ${pluralize('ramp', segments.length)}`
        : '',
    ]
      .filter(Boolean)
      .join(' | '),
    amount: formatCurrency(amount, currency ? { currency } : {}),
  };
};

/**
 * Subtotal for a staged row's membership — the same figure, and the same
 * formatting, the quote's own group container header carries.
 *
 * Summed from the flat offering list rather than the table's parent/child tree,
 * and off the draft rather than the server, so a card's total tracks offerings
 * as they are moved in and out of it. A member's ramp/scheduled-change segments
 * count toward it: they follow their parent into the group, and the draft holds
 * root ids only, so they are matched by parent id.
 */
export const getManageGroupsRowSubtotal = (
  quoteOfferingIds: string[],
  quoteOfferings: Maybe<IQuoteOfferingRespSchema[]>,
  currency: Maybe<string>,
) => {
  const memberIds = new Set(quoteOfferingIds);
  const subtotal = (quoteOfferings || []).reduce(
    (total, { id, parentQuoteOfferingId, amount }) =>
      memberIds.has(parentQuoteOfferingId || id)
        ? total + (amount || 0)
        : total,
    0,
  );
  return formatQuoteSubtotal(subtotal, currency);
};

/** Rebuild the draft from server state — on open, and after a partial save */
export const seedManageGroupsDraftRows = (
  groups: Maybe<QuoteOfferingGroup[]>,
): ManageGroupsDraftRow[] =>
  (groups || []).map((group) => ({
    key: group.id,
    groupId: group.id,
    referenceGroupId: group.referenceGroupId,
    groupType:
      group.groupType || QuoteOfferingGroupTypeSchema.ACCOUNT_HIERARCHY,
    shippingAccountId: group.shippingAccount?.id ?? '',
    shippingContactId: group.shippingContactId ?? '',
    billingAccountId: group.billingAccount?.id ?? '',
    billingContactId: group.billingContactId ?? '',
    quoteOfferingIds: [...group.quoteOfferingIds],
    customFields: group.customFields,
  }));

/**
 * A new row pre-filled from the quote's contacts (primary = shipping, billing =
 * billing), so rows start valid and the user only edits what differs.
 *
 * When the quote has no contact set for a side, the account still falls back to
 * the quote's own account rather than being left blank. A blank account disables
 * the contact picker, which left the user with a row they could neither fill in
 * nor save; seeding the account lets them just choose a contact.
 */
export const buildNewManageGroupsDraftRow = (
  quote: Maybe<IQuoteRespSchema>,
  key: string,
): ManageGroupsDraftRow => ({
  key,
  groupType: QuoteOfferingGroupTypeSchema.ACCOUNT_HIERARCHY,
  shippingAccountId:
    quote?.contacts?.primary?.accountId || quote?.accountId || '',
  shippingContactId: quote?.contacts?.primary?.id || '',
  billingAccountId:
    quote?.contacts?.billing?.accountId || quote?.accountId || '',
  billingContactId: quote?.contacts?.billing?.id || '',
  quoteOfferingIds: [],
});

export const buildManageGroupsRowPayload = (
  row: ManageGroupsDraftRow,
  quoteOfferings: Maybe<IQuoteOfferingRespSchema[]>,
): QuoteOfferingGroupRequest => ({
  groupType: row.groupType,
  billingAccountId: row.billingAccountId,
  shippingAccountId: row.shippingAccountId,
  billingContactId: row.billingContactId,
  shippingContactId: row.shippingContactId,
  customFields: row.customFields ?? undefined,
  quoteOfferingIds: sanitizeQuoteOfferingGroupMemberIds(
    row.quoteOfferingIds,
    quoteOfferings,
  ),
});

const hasRowDiverged = (
  payload: QuoteOfferingGroupRequest,
  group: QuoteOfferingGroup,
  quoteOfferings: Maybe<IQuoteOfferingRespSchema[]>,
) => {
  const [serverRow] = seedManageGroupsDraftRows([group]);
  const serverPayload = buildManageGroupsRowPayload(serverRow, quoteOfferings);
  return (
    payload.shippingAccountId !== serverPayload.shippingAccountId ||
    payload.shippingContactId !== serverPayload.shippingContactId ||
    payload.billingAccountId !== serverPayload.billingAccountId ||
    payload.billingContactId !== serverPayload.billingContactId ||
    // Normalized because "no custom fields" arrives as both undefined and {}
    !isEqual(payload.customFields ?? {}, serverPayload.customFields ?? {}) ||
    // Membership is a set; ordering carries no meaning
    !isEqual(
      [...payload.quoteOfferingIds].sort(),
      [...serverPayload.quoteOfferingIds].sort(),
    )
  );
};

/**
 * Diff the staged rows against the server's groups.
 *
 * Applied deletes → updates → creates, but the order doesn't actually matter:
 * the draft guarantees an offering sits in at most one row, and the backend
 * evicts an offering from its previous group whenever another group claims it.
 * A group whose members merely moved to another group therefore gets a
 * redundant-but-harmless PUT rather than special-casing.
 */
export const buildManageGroupsDraftPlan = ({
  rows,
  serverGroups,
  quoteOfferings,
}: {
  rows: ManageGroupsDraftRow[];
  serverGroups: Maybe<QuoteOfferingGroup[]>;
  quoteOfferings: Maybe<IQuoteOfferingRespSchema[]>;
}): ManageGroupsDraftPlan => {
  const plan: ManageGroupsDraftPlan = {
    deletes: [],
    updates: [],
    creates: [],
  };
  const groupById = new Map(
    (serverGroups || []).map((group) => [group.id, group]),
  );
  const stagedGroupIds = new Set(
    rows.map(({ groupId }) => groupId).filter(Boolean),
  );

  (serverGroups || []).forEach((group) => {
    if (!stagedGroupIds.has(group.id)) {
      plan.deletes.push({
        quoteOfferingGroupId: group.id,
        label: getQuoteOfferingGroupLabel(group),
      });
    }
  });

  rows.forEach((row, rowIndex) => {
    const payload = buildManageGroupsRowPayload(row, quoteOfferings);
    const label = getManageGroupsRowLabel(rowIndex);
    const group = row.groupId ? groupById.get(row.groupId) : undefined;
    if (!group) {
      plan.creates.push({ rowKey: row.key, label, payload });
    } else if (hasRowDiverged(payload, group, quoteOfferings)) {
      plan.updates.push({
        quoteOfferingGroupId: group.id,
        rowKey: row.key,
        label,
        payload,
      });
    }
  });

  return plan;
};

/**
 * Pending change per row, so a card can say whether it is new or edited. Without
 * it the only feedback is a total count, and a screen of identical cards gives no
 * clue which ones were actually touched.
 */
export const getManageGroupsRowStatuses = ({
  updates,
  creates,
}: ManageGroupsDraftPlan) => {
  const statusByRowKey = new Map<string, ManageGroupsRowStatus>();
  creates.forEach(({ rowKey }) => statusByRowKey.set(rowKey, 'new'));
  updates.forEach(({ rowKey }) => statusByRowKey.set(rowKey, 'edited'));
  return statusByRowKey;
};

export const hasManageGroupsDraftChanges = ({
  deletes,
  updates,
  creates,
}: ManageGroupsDraftPlan) =>
  deletes.length > 0 || updates.length > 0 || creates.length > 0;

export const countManageGroupsDraftChanges = ({
  deletes,
  updates,
  creates,
}: ManageGroupsDraftPlan) => deletes.length + updates.length + creates.length;
